import { css } from '@genesislcap/web-core';
import { cancelButtonBg, closeIconStyles } from '../../../../styles';
import { sharedMuniGridStyles } from '../../deals.styles';
import { DEAL_SERIES_COLUMN_WIDTHS, dealSeriesBorder } from '../deal-series.col-defs';
import { quantityValidationGridSlottedStyles } from '../deal-series.quantity-validation';
import { TERM_BOND_PAR_AMOUNT_COLOR } from '../sinking-fund-schedule.util';

/** Split-order modal columns not present on the maturity grid. */
export const SPLIT_ORDER_ONLY_COLUMN_WIDTHS = {
  rowActions: DEAL_SERIES_COLUMN_WIDTHS.actions,
} as const;

/** Fixed column widths for the split-order grid (shared cols from deal-series.col-defs). */
export const SPLIT_ORDER_COL_WIDTHS = {
  maturity: DEAL_SERIES_COLUMN_WIDTHS.maturity,
  putDate: DEAL_SERIES_COLUMN_WIDTHS.putDate,
  parAmount: DEAL_SERIES_COLUMN_WIDTHS.parAmount,
  coupon: DEAL_SERIES_COLUMN_WIDTHS.coupon,
  yield: DEAL_SERIES_COLUMN_WIDTHS.yield,
  spread: DEAL_SERIES_COLUMN_WIDTHS.spread,
  price: DEAL_SERIES_COLUMN_WIDTHS.price,
  ustSpread: DEAL_SERIES_COLUMN_WIDTHS.ustSpread,
  ustBenchmark: DEAL_SERIES_COLUMN_WIDTHS.ustBenchmark,
  notes: DEAL_SERIES_COLUMN_WIDTHS.notes,
  splitAmount: DEAL_SERIES_COLUMN_WIDTHS.orderAmount,
  conditions: DEAL_SERIES_COLUMN_WIDTHS.conditionsSummary,
  orderStatus: DEAL_SERIES_COLUMN_WIDTHS.orderStatus,
  ...SPLIT_ORDER_ONLY_COLUMN_WIDTHS,
} as const;

/** Horizontal padding on modal content (16px each side). */
export const SPLIT_ORDER_MODAL_HORIZONTAL_PADDING = 32;

/** Max grid width when all optional read-only columns are shown (put + taxable economics). */
export const SPLIT_ORDER_MODAL_MAX_GRID_WIDTH =
  SPLIT_ORDER_COL_WIDTHS.maturity +
  SPLIT_ORDER_COL_WIDTHS.putDate +
  SPLIT_ORDER_COL_WIDTHS.parAmount +
  SPLIT_ORDER_COL_WIDTHS.coupon +
  SPLIT_ORDER_COL_WIDTHS.yield +
  SPLIT_ORDER_COL_WIDTHS.spread +
  SPLIT_ORDER_COL_WIDTHS.price +
  SPLIT_ORDER_COL_WIDTHS.ustSpread +
  SPLIT_ORDER_COL_WIDTHS.ustBenchmark +
  SPLIT_ORDER_COL_WIDTHS.notes +
  SPLIT_ORDER_COL_WIDTHS.splitAmount +
  SPLIT_ORDER_COL_WIDTHS.conditions +
  SPLIT_ORDER_COL_WIDTHS.orderStatus +
  SPLIT_ORDER_COL_WIDTHS.rowActions;

/** Upper-bound modal dialog width (actual width is set via --split-order-modal-width). */
export const SPLIT_ORDER_MODAL_MAX_WIDTH =
  SPLIT_ORDER_MODAL_MAX_GRID_WIDTH + SPLIT_ORDER_MODAL_HORIZONTAL_PADDING;

/** CSS length for dialog width upper bound (keep in sync with SPLIT_ORDER_MODAL_MAX_WIDTH). */
const splitOrderModalDialogWidth = `${SPLIT_ORDER_MODAL_MAX_WIDTH}px`;

/** Minimum modal height. */
export const SPLIT_ORDER_MODAL_MIN_HEIGHT = 600;

/** Maximum modal height; grid scrolls internally above this limit. */
export const SPLIT_ORDER_MODAL_MAX_HEIGHT = 750;

const splitOrderModalMinHeight = `${SPLIT_ORDER_MODAL_MIN_HEIGHT}px`;
const splitOrderModalMaxHeight = `${SPLIT_ORDER_MODAL_MAX_HEIGHT}px`;

export const splitOrderModalStyles = css`
  :host {
    display: none;
  }

  :host([open]) {
    display: block;
  }

  ${sharedMuniGridStyles}
  ${closeIconStyles}

  .split-order-modal::part(close-icon) {
    font-size: var(--type-ramp-base-font-size);
    --base-height-multiplier: 6;
  }

  .split-order-modal::part(dialog) {
    width: min(var(--split-order-modal-width, ${splitOrderModalDialogWidth}), 96vw);
    min-width: unset;
    max-width: 96vw;
    min-height: ${splitOrderModalMinHeight};
    max-height: min(${splitOrderModalMaxHeight}, 92vh);
    overflow: hidden;
    background: #1f2126;
    border: 1px solid ${cancelButtonBg};
    border-radius: 6px;
    display: flex;
    flex-direction: column;
  }

  .split-order-modal::part(content) {
    width: 100%;
    box-sizing: border-box;
    padding: 0 16px;
    overflow: hidden;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
  }

  .split-order-modal-content {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    position: relative;
    overflow: hidden;
    flex: 1 1 auto;
  }

  .split-order-modal-body {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow: hidden;
    flex: 1 1 auto;
  }

  .split-order-grid-container {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  .split-order-grid-container rapid-grid-pro {
    width: 100%;
    height: 100%;
    display: block;
  }

  .split-order-modal-content rapid-grid-pro::part(grid) {
    --ag-row-border-color: ${dealSeriesBorder};
    --ag-header-background-color: transparent;
    width: 100%;
  }

  .split-order-add-row {
    width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
  }

  .split-order-add {
    color: var(--neutral-foreground-rest, #f1f1f1);
    cursor: pointer;
    font-size: 14px;
    width: fit-content;
    user-select: none;
    padding: 4px 0;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .split-order-add:hover {
    color: #fff;
  }

  .split-order-modal-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 12px 0 16px;
    flex-shrink: 0;
    box-sizing: border-box;
  }

  .split-order-validation-message {
    margin-right: auto;
    color: #ffb660;
    font-size: 12px;
    line-height: 1.3;
  }

  .split-cancel-button::part(control) {
    background: transparent;
    border: 1px solid ${cancelButtonBg};
    color: #f1f1f1;
  }

  .split-cancel-button::part(control):hover {
    background: ${cancelButtonBg};
  }

  .conditions-popup {
    background: #1f2126;
    border: 1px solid ${cancelButtonBg};
    border-radius: 6px;
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    max-height: 100%;
    min-width: 760px;
    max-width: calc(100% - 40px);
    padding: 10px;
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    z-index: 2;

    .conditions-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      flex-shrink: 0;

      > div:first-child {
        flex: 1;
      }
    }

    .conditions-form {
      flex: 1;
      min-height: 0;
      overflow-x: visible;
      overflow-y: auto;
    }
  }
`;

export const splitOrderModalSlottedStyles = `
  ${quantityValidationGridSlottedStyles}

  .ag-cell.term-bond-par-amount {
    color: ${TERM_BOND_PAR_AMOUNT_COLOR} !important;
  }

  .ag-header,
  .ag-floating-top,
  .ag-row-pinned {
    background-color: #1f2126;
  }

  /* Pinned parent: AG Grid row hover is a ::before overlay using --ag-row-hover-color */
  .ag-floating-top {
    --ag-row-hover-color: transparent;
  }

  .grouped-column {
    border-right: 1px solid rgba(255, 255, 255, 0.3);
  }

  /* Header + pinned parent row — white column guides */
  .ag-header-cell:not([col-id="rowActions"]) {
    border-right: 1px solid rgba(255, 255, 255, 0.3) !important;
    box-sizing: border-box !important;
  }

  .ag-header-cell[col-id="splitAmount"] {
    border-left: 1px solid rgba(255, 255, 255, 0.3) !important;
  }

  .ag-floating-top .ag-row .ag-cell:not([col-id="rowActions"]) {
    border-right: 1px solid rgba(255, 255, 255, 0.3) !important;
    box-sizing: border-box !important;
  }

  .ag-floating-top .ag-row .ag-cell[col-id="splitAmount"] {
    border-left: 1px solid rgba(255, 255, 255, 0.3) !important;
  }

  .ag-header-cell[col-id="rowActions"],
  .ag-floating-top .ag-row .ag-cell[col-id="rowActions"] {
    border-right: none !important;
  }

  .ag-cell.hover-effect:hover {
    border: 1px solid rgb(255, 255, 255) !important;
    box-sizing: border-box !important;
  }

  /* Validation states must override hover-effect — higher specificity + later in cascade */
  .ag-cell.hover-effect.quantity-error,
  .ag-cell.hover-effect.quantity-error:hover {
    border: 1px solid var(--error-color, #ff4444) !important;
    box-sizing: border-box !important;
  }

  .ag-cell.hover-effect.quantity-warning,
  .ag-cell.hover-effect.quantity-warning:hover {
    border: 1px solid #ffb660 !important;
    box-sizing: border-box !important;
  }

  .ag-cell.hover-effect:focus-within {
    border: 1px solid var(--ag-range-selection-border-color) !important;
    background-color: #181d1f !important;
  }

  .ag-cell.quantity-error:focus-within {
    border: 1px solid var(--error-color, #ff4444) !important;
    box-sizing: border-box !important;
  }

  .ag-cell.quantity-warning:focus-within {
    border: 1px solid #ffb660 !important;
    box-sizing: border-box !important;
  }

  .ag-cell-focus {
    border: none !important;
  }

  .focus-border {
    border: 1px solid #505050 !important;
    box-sizing: border-box !important;
  }

  .conditions-summary-cell {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    line-height: 1.2;
    padding: 4px 8px;
    cursor: pointer;
  }

  .ag-row.conditions-modal-open .conditions-summary-cell {
    border: 1px solid var(--accent-fill-rest, #1f2126) !important;
    box-sizing: border-box !important;
  }

  .ag-cell.conditions-summary-cell:not(.hover-effect) {
    cursor: default !important;
  }

  .split-remove-row-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    color: #fff;
    font-size: 20px;
    font-weight: 300;
    line-height: 1;
    cursor: pointer;
    border-radius: 3px;
    flex-shrink: 0;
  }

  .split-remove-row-btn:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  .ag-cell[col-id="splitAmount"] {
    overflow: hidden !important;
    padding: 0 !important;
  }

  .ag-cell[col-id="splitAmount"].split-amount-total-cell {
    color: #fff;
    font-size: 14px;
  }

  .ag-cell[col-id="splitAmount"] .ag-cell-wrapper,
  .ag-cell[col-id="splitAmount"] .ag-cell-value {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .ag-cell[col-id="conditions"] {
    overflow: hidden !important;
  }

  .split-order-amount-host {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    padding: 0 8px;
    overflow: hidden;
  }

  .split-order-amount-host maturity-amount-entry {
    flex: 1;
    min-width: 0;
    height: 100%;
  }

  .split-order-amount-host.is-in-transit maturity-amount-entry rapid-button {
    opacity: 0.35;
    pointer-events: none;
  }

  .split-row-actions-host {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
  }

  .ag-cell[col-id="rowActions"] {
    overflow: visible !important;
    padding: 0 !important;
  }

  .ag-cell[col-id="rowActions"] .ag-cell-wrapper,
  .ag-cell[col-id="rowActions"] .ag-cell-value {
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  .split-row-action-icon {
    height: 14px;
    width: auto;
    cursor: pointer;
    padding: 5px;
    border-radius: 5px;
    box-sizing: content-box;
    opacity: 0.85;
  }

  .split-row-action-icon:hover {
    background: rgba(255, 255, 255, 0.2);
    opacity: 1;
  }

  .split-row-action-icon.is-dimmed {
    opacity: 0.35;
    pointer-events: none;
  }

  .split-conditions-placeholder {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #fff;
    font-size: 14px;
  }

  .split-conditions-placeholder img {
    width: 20px;
    height: 20px;
  }

  .split-conditions-readonly {
    cursor: default;
    pointer-events: none;
  }

  /* Pinned parent row — block clicks except MATURITY_AMOUNT (sinking fund / principal popups) and splitAmount tooltips */
  .ag-floating-top .ag-row .ag-cell {
    pointer-events: none !important;
    cursor: default !important;
  }

  .ag-floating-top .ag-row .ag-cell[col-id="MATURITY_AMOUNT"],
  .ag-floating-top .ag-row .ag-cell[col-id="splitAmount"] {
    pointer-events: auto !important;
    cursor: default !important;
  }

  /* Parent total is read-only: hover the cell for validation tooltip, not the inner entry */
  .ag-floating-top .ag-cell[col-id="splitAmount"] .split-order-amount-host,
  .ag-floating-top .ag-cell[col-id="splitAmount"] .split-order-amount-host * {
    pointer-events: none !important;
  }

  .ag-floating-top .ag-row.ag-row-hover::before,
  .ag-floating-top .ag-row-hover.ag-full-width-row.ag-row-group::before {
    background-color: transparent !important;
    background-image: none !important;
  }

`;
